import { can, formatDate } from '@clim/client-core';
import { PORTAL_DEFAULT_DAYS, portalLinkState, shareLinks, type DossierRecord, type PortalChannel } from '@clim/domain';
import { useState } from 'react';
import { errorMessage } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { Empty, ErrorBox, Field, Modal, Select } from './ui';

const STATE_LABEL = { active: 'actif', expired: 'expiré', revoked: 'révoqué' } as const;

/** Lien sécurisé vers le portail client (CDC §5) : SMS, WhatsApp, e-mail — préparable hors ligne. */
export function PortalLinks({ dossier }: { dossier: DossierRecord }) {
  const engine = useEngine();
  const { me } = useReady();
  const [creating, setCreating] = useState(false);
  const [sharing, setSharing] = useState<{ id: string; token: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!can(me, 'portal.link.manage')) return null;
  const links = engine
    .list('portal_link')
    .filter((l) => l.dossierId === dossier.id)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const closed = ['cancelled', 'completed'].includes(dossier.status);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Portail client</h2>
        {!closed && (
          <button className="btn small primary" onClick={() => setCreating(true)}>
            ✉ Envoyer un lien au client
          </button>
        )}
      </div>
      <p className="muted">Le client complète ses coordonnées, dépose ses documents et signe à distance. Lien limité à ce dossier, expirable et révocable.</p>
      {links.length === 0 ? (
        <Empty>Aucun lien envoyé.</Empty>
      ) : (
        <ul className="plain files">
          {links.map((l) => {
            const state = portalLinkState(l);
            const token = engine.portalLinkToken(l.id);
            return (
              <li key={l.id}>
                <div className="row-main">
                  <strong>
                    Lien {STATE_LABEL[state]}
                    {engine.syncState('portal_link', l.id) !== 'synced' && <span className="sync-tag"> actif après synchronisation</span>}
                  </strong>
                  <span className="muted">
                    créé le {formatDate(l.createdAt)} par {l.createdByName} · expire le {formatDate(l.expiresAt)} · ouvert {l.useCount} fois
                  </span>
                </div>
                {state === 'active' && token && (
                  <button className="btn small" onClick={() => setSharing({ id: l.id, token })}>
                    Partager
                  </button>
                )}
                {state === 'active' && (
                  <button className="btn small ghost" onClick={() => confirm('Révoquer ce lien ? Le client ne pourra plus l’utiliser.') && void engine.revokePortalLink(l.id).catch((e) => setError(errorMessage(e)))}>
                    Révoquer
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <ErrorBox message={error} />
      {creating && (
        <CreateLink
          dossier={dossier}
          onClose={(created) => {
            setCreating(false);
            if (created) setSharing(created);
          }}
        />
      )}
      {sharing && <ShareLink dossier={dossier} token={sharing.token} onClose={() => setSharing(null)} />}
    </div>
  );
}

function CreateLink({ dossier, onClose }: { dossier: DossierRecord; onClose(created?: { id: string; token: string }): void }) {
  const engine = useEngine();
  const { me } = useReady();
  const [days, setDays] = useState(PORTAL_DEFAULT_DAYS);
  const [rights, setRights] = useState({ allowEdit: true, allowUpload: true, allowSign: true });
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal title="Lien sécurisé pour le client" onClose={() => onClose()}>
      <div className="form">
        <Field label="Validité">
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[1, 3, 7, 14, 30].map((n) => (
              <option key={n} value={n}>
                {n} jour{n > 1 ? 's' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <label className="check">
          <input type="checkbox" checked={rights.allowEdit} onChange={(e) => setRights({ ...rights, allowEdit: e.target.checked })} />
          Compléter ses coordonnées
        </label>
        <label className="check">
          <input type="checkbox" checked={rights.allowUpload} onChange={(e) => setRights({ ...rights, allowUpload: e.target.checked })} />
          Déposer des documents / photos
        </label>
        <label className="check">
          <input type="checkbox" checked={rights.allowSign} onChange={(e) => setRights({ ...rights, allowSign: e.target.checked })} />
          Signer à distance (quand le dossier est prêt)
        </label>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={() => onClose()}>
            Annuler
          </button>
          <button
            className="btn primary"
            onClick={() =>
              engine
                .createPortalLink(dossier.id, { tenantId: me.tenant.id, days, ...rights })
                .then(onClose)
                .catch((e) => setError(errorMessage(e)))
            }
          >
            Créer le lien
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ShareLink({ dossier, token, onClose }: { dossier: DossierRecord; token: string; onClose(): void }) {
  const engine = useEngine();
  const { me } = useReady();
  const [copied, setCopied] = useState(false);
  const client = engine.get('client', dossier.clientId);
  const territory = engine.get('territory', dossier.territoryId);
  const url = `${window.location.origin}/portail#${token}`;
  const links = shareLinks(url, { phone: client?.phone, email: client?.email, dialCode: territory?.settings.dialCode as string | undefined, company: me.tenant.name });
  const open = (href: string, _channel: PortalChannel) => window.open(href, '_blank', 'noopener');
  return (
    <Modal title="Envoyer le lien" onClose={onClose}>
      <p>
        Lien personnel du client (ne le publiez pas) :<br />
        <code className="link-box" aria-label="Lien du portail">
          {url}
        </code>
      </p>
      {engine.pendingCount > 0 && <p className="sync-tag">Hors ligne : le lien sera actif dès la synchronisation.</p>}
      <div className="actions wrap">
        <button className="btn primary" onClick={() => open(links.sms, 'sms')}>
          💬 SMS
        </button>
        <button className="btn primary" onClick={() => open(links.whatsapp, 'whatsapp')}>
          WhatsApp
        </button>
        <button className="btn primary" onClick={() => open(links.email, 'email')}>
          ✉ E-mail
        </button>
        <button
          className="btn"
          onClick={() =>
            navigator.clipboard.writeText(url).then(
              () => setCopied(true),
              () => setCopied(false),
            )
          }
        >
          {copied ? 'Copié ✓' : 'Copier le lien'}
        </button>
        {'share' in navigator && (
          <button className="btn" onClick={() => void navigator.share({ title: me.tenant.name, text: links.text, url }).catch(() => undefined)}>
            Partager…
          </button>
        )}
      </div>
    </Modal>
  );
}
